/* ==========================================================================
   hero-webgl.js — cursor-driven displacement on the hero image
   (SPEC v4 §3.3, DECISIONS D-021). Raw WebGL, no library.
   Gates: pointer-fine desktop, no reduced-motion, image hero only.
   Fail-silent everywhere: the plain <img> stays underneath; the canvas is
   a decorative overlay (aria-hidden) that never touches the LCP path —
   this module loads after idle from theme.liquid.
   ========================================================================== */

(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const heroMedia = document.querySelector('.hero__media');
  const img = heroMedia?.querySelector('img');
  if (!heroMedia || !img || heroMedia.querySelector('video')) return;

  const VERT = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main() {
      vUv = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }`;

  /* Ripple: gentle radial displacement around the cursor + slow idle drift */
  const FRAG = `
    precision mediump float;
    varying vec2 vUv;
    uniform sampler2D uTex;
    uniform vec2 uMouse;
    uniform float uStrength;
    uniform float uTime;
    uniform vec2 uCover;   /* cover-fit scale */
    uniform vec2 uOffset;  /* cover-fit offset */
    void main() {
      vec2 uv = vUv * uCover + uOffset;
      vec2 toMouse = vUv - uMouse;
      float dist = length(toMouse);
      float ripple = smoothstep(0.45, 0.0, dist) * uStrength;
      vec2 drift = vec2(
        sin(uTime * 0.12 + vUv.y * 4.0),
        cos(uTime * 0.1 + vUv.x * 4.0)
      ) * 0.0015;
      uv += normalize(toMouse + 0.0001) * ripple * -0.02 + drift;
      gl_FragColor = texture2D(uTex, clamp(uv, 0.001, 0.999));
    }`;

  const start = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.className = 'hero__webgl';
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
      const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
      if (!gl) return;

      const compile = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error('shader');
        return s;
      };
      const prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
      gl.useProgram(prog);

      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      const uMouse = gl.getUniformLocation(prog, 'uMouse');
      const uStrength = gl.getUniformLocation(prog, 'uStrength');
      const uTime = gl.getUniformLocation(prog, 'uTime');
      const uCover = gl.getUniformLocation(prog, 'uCover');
      const uOffset = gl.getUniformLocation(prog, 'uOffset');

      const texImg = new Image();
      texImg.crossOrigin = 'anonymous';
      texImg.src = img.currentSrc || img.src;
      texImg.decode().then(() => {
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, texImg);
        heroMedia.append(canvas);

        let mx = 0.5, my = 0.5, tx = 0.5, ty = 0.5, strength = 0, target = 0;
        let visible = true, raf;

        const size = () => {
          const dpr = Math.min(window.devicePixelRatio, 1.75);
          canvas.width = heroMedia.clientWidth * dpr;
          canvas.height = heroMedia.clientHeight * dpr;
          gl.viewport(0, 0, canvas.width, canvas.height);
          /* cover-fit math */
          const cAspect = canvas.width / canvas.height;
          const iAspect = texImg.width / texImg.height;
          let sx = 1, sy = 1;
          if (cAspect > iAspect) sy = iAspect / cAspect;
          else sx = cAspect / iAspect;
          gl.uniform2f(uCover, sx, sy);
          gl.uniform2f(uOffset, (1 - sx) / 2, (1 - sy) / 2);
        };
        size();
        new ResizeObserver(size).observe(heroMedia);

        heroMedia.addEventListener('pointermove', (e) => {
          const r = heroMedia.getBoundingClientRect();
          tx = (e.clientX - r.left) / r.width;
          ty = 1 - (e.clientY - r.top) / r.height;
          target = 1;
        });
        heroMedia.addEventListener('pointerleave', () => (target = 0));

        new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0 }).observe(heroMedia);

        const t0 = performance.now();
        const frame = (now) => {
          raf = requestAnimationFrame(frame);
          if (!visible) return;
          mx += (tx - mx) * 0.08;
          my += (ty - my) * 0.08;
          strength += (target - strength) * 0.06;
          gl.uniform2f(uMouse, mx, my);
          gl.uniform1f(uStrength, strength);
          gl.uniform1f(uTime, (now - t0) / 1000);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
        };
        raf = requestAnimationFrame(frame);

        document.addEventListener('shopify:section:unload', () => cancelAnimationFrame(raf));
      }).catch(() => canvas.remove());
    } catch {
      /* decorative layer only — the image hero stands (SPEC §23.2) */
    }
  };

  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 4000 });
  else setTimeout(start, 2000);
})();
